import { motion } from "framer-motion";
import { Zap, ShieldCheck, Handshake, MessageSquare, Sparkles, Crosshair } from "lucide-react";
import { useLang } from "@/i18n";

const ARCH_IMG =
  "https://images.unsplash.com/photo-1623051786552-e46ef84e6c07?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTV8MHwxfHNlYXJjaHwyfHxwcmVtaXVtJTIwY29ycG9yYXRlJTIwb2ZmaWNlJTIwYnVpbGRpbmclMjBhcmNoaXRlY3R1cmV8ZW58MHx8fHwxNzg2ODQxMTUyfDA&ixlib=rb-4.1.0&q=85";

const reveal = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] },
};

export default function WhyUs() {
  const { t } = useLang();

  const features = [
    { icon: Zap, title: t("why_c1t"), text: t("why_c1x"), span: "md:col-span-5" },
    { icon: ShieldCheck, title: t("why_c2t"), text: t("why_c2x"), span: "md:col-span-3" },
    { icon: Handshake, title: t("why_c3t"), text: t("why_c3x"), span: "md:col-span-4" },
  ];

  const trust = [
    { icon: MessageSquare, title: t("trust_1t"), text: t("trust_1x") },
    { icon: Sparkles, title: t("trust_2t"), text: t("trust_2x") },
    { icon: Crosshair, title: t("trust_3t"), text: t("trust_3x") },
  ];
  return (
    <section id="why-us" className="py-28 lg:py-40">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <motion.div {...reveal} className="max-w-2xl">
          <p className="overline-label" data-testid="why-us-overline">{t("why_over")}</p>
          <h2 className="mt-6 font-serif font-semibold text-4xl sm:text-5xl tracking-tight leading-tight" data-testid="why-us-heading">
            {t("why_h1a")} <span className="text-brand-300">{t("why_h1b")}</span> {t("why_h1c")}
          </h2>
        </motion.div>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-12 gap-6">
          {features.map(({ icon: Icon, title, text, span }, i) => (
            <motion.div
              key={title}
              {...reveal}
              transition={{ ...reveal.transition, delay: i * 0.12 }}
              className={`${span} group bg-navy-800 border border-brand/10 p-10 hover:-translate-y-1 hover:border-brand/30 hover:shadow-2xl transition-all duration-500`}
              data-testid={`why-us-card-${i}`}
            >
              <span className="inline-flex items-center justify-center w-12 h-12 border border-brand/30 text-brand transition-colors duration-500 group-hover:bg-brand group-hover:text-white">
                <Icon size={22} />
              </span>
              <h3 className="mt-8 font-serif text-2xl sm:text-3xl text-cream">{title}</h3>
              <p className="mt-4 text-sm sm:text-base text-cream/55 leading-relaxed">{text}</p>
            </motion.div>
          ))}

          <motion.div
            initial={{ opacity: 0, clipPath: "inset(0 100% 0 0)" }}
            whileInView={{ opacity: 1, clipPath: "inset(0 0% 0 0)" }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
            className="md:col-span-7 relative overflow-hidden border border-brand/10 min-h-[16rem] group"
            data-testid="why-us-image-tile"
          >
            <img
              src={ARCH_IMG}
              alt="Premium corporate architecture"
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-navy-900/90 via-navy-900/40 to-transparent" />
            <div className="relative p-10 flex flex-col justify-end h-full min-h-[16rem]">
              <p className="overline-label">{t("why_tile_over")}</p>
              <p className="mt-3 font-serif text-2xl sm:text-3xl text-cream max-w-md">
                {t("why_tile_x")}
              </p>
            </div>
          </motion.div>

          <motion.div
            {...reveal}
            transition={{ ...reveal.transition, delay: 0.4 }}
            className="md:col-span-5 bg-brand p-10 flex flex-col justify-between hover:-translate-y-1 transition-transform duration-500"
            data-testid="why-us-cta-tile"
          >
            <h3 className="font-serif text-3xl text-white leading-tight">
              {t("why_cta_h")}
            </h3>
            <a
              href="#plans"
              data-testid="why-us-view-plans-btn"
              className="mt-8 inline-block self-start bg-navy-900 text-brand-300 text-xs uppercase tracking-[0.2em] font-bold px-8 py-4 hover:bg-navy-700 transition-colors duration-300"
            >
              {t("why_cta_btn")}
            </a>
          </motion.div>
        </div>

        <div className="mt-28">
          <motion.div {...reveal} className="flex items-end justify-between flex-wrap gap-6">
            <h3 className="font-serif font-semibold text-3xl sm:text-4xl" data-testid="why-trust-heading">
              {t("trust_h1")} <span className="text-brand-300">{t("trust_h2")}</span>
            </h3>
            <span className="hidden sm:block h-px flex-1 mx-10 bg-brand/15" aria-hidden="true" />
          </motion.div>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
            {trust.map(({ icon: Icon, title, text }, i) => (
              <motion.div
                key={title}
                {...reveal}
                transition={{ ...reveal.transition, delay: i * 0.12 }}
                className="border-l-2 border-brand/40 pl-8 py-2 hover:border-brand transition-colors duration-500"
                data-testid={`why-trust-item-${i}`}
              >
                <Icon size={20} className="text-brand" />
                <h4 className="mt-4 font-serif text-xl sm:text-2xl text-cream">{title}</h4>
                <p className="mt-3 text-sm text-cream/55 leading-relaxed">{text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
